import React from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Collapse,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {findPrintoutVariable} from "../../../variables/printoutVariables";
import type {PrintoutDateFormat} from "../../../variables/printoutVariables";
import {
    createDataListId,
    DATA_LIST_AGENDA_SECTOR_TYPES,
    DATA_LIST_ALIGNS,
    getDataListAttrs,
    getDataListColumnLabel,
    getDataListDefaultAlign,
    getDataListSourceDefinition,
    getDataListVariableLabel,
} from "../../../blocks/dataList/printoutDataListOptions";
import type {
    DataListAgendaSectorType,
    DataListAlign,
    DataListColumn,
    DataListTextStyle,
    PrintoutDataListAttrs,
} from "../../../blocks/dataList/printoutDataListOptions";
import {PanelField, PanelHint} from "../PrintoutPanelSection";
import PrintoutSettingsAccordion from "./PrintoutSettingsAccordion";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";
import type {PrintoutBlockSettingsProps} from "./printoutBlockAttributes";
import {DATE_FORMAT_OPTIONS} from "./printoutVariableFormatOptions";

const ALIGN_ICONS: Record<DataListAlign, React.ReactNode> = {
    center: <FormatAlignCenterIcon fontSize={'small'}/>,
    left: <FormatAlignLeftIcon fontSize={'small'}/>,
    right: <FormatAlignRightIcon fontSize={'small'}/>,
};

const AGENDA_SECTOR_TYPE_LABELS: Record<DataListAgendaSectorType, string> = {
    OTHER: 'Other',
    RESTAURANT: 'Restaurant',
    SPA: 'Spa',
    WEEKLY_PLANNER: 'Weekly Planner',
};

interface VariableOption {
    key: string;
    label: string;
    recommended: boolean;
}

interface ColumnMenuState {
    anchorEl: HTMLElement;
    columnId: string;
}

const RowCaption = ({children}: {children: React.ReactNode}) => (
    <Typography color={'text.secondary'} sx={{fontSize: 11, lineHeight: 1.4}}>
        {children}
    </Typography>
);

const FormatToggle = ({disabled, onChange, value}: {
    disabled?: boolean;
    onChange: (style: DataListTextStyle) => void;
    value: DataListTextStyle;
}) => {
    const {t} = useTranslation();
    const selected = [value.bold ? 'bold' : '', value.italic ? 'italic' : ''].filter(Boolean);

    return (
        <ToggleButtonGroup
            disabled={disabled}
            onChange={(_event, next: string[]) => onChange({
                bold: next.includes('bold'),
                italic: next.includes('italic'),
            })}
            size={'small'}
            value={selected}
        >
            <ToggleButton aria-label={t('Bold')} value={'bold'}>
                <FormatBoldIcon fontSize={'small'}/>
            </ToggleButton>
            <ToggleButton aria-label={t('Italic')} value={'italic'}>
                <FormatItalicIcon fontSize={'small'}/>
            </ToggleButton>
        </ToggleButtonGroup>
    );
};

export function DataListSettings({block, disabled, editor}: PrintoutBlockSettingsProps) {
    const {t} = useTranslation();
    const attrs = getDataListAttrs(block.node.attrs as PrintoutDataListAttrs);
    const sourceDefinition = getDataListSourceDefinition(attrs.source);
    const [columnMenu, setColumnMenu] = React.useState<ColumnMenuState | null>(null);
    const [expandedColumnId, setExpandedColumnId] = React.useState<string | null>(null);
    const [showAddField, setShowAddField] = React.useState(false);

    const usedKeys = new Set(attrs.columns.map(column => column.variableKey));
    const recommendedKeys = new Set(sourceDefinition.defaultColumnKeys);
    const menuColumnIndex = columnMenu
        ? attrs.columns.findIndex(column => column.id === columnMenu.columnId)
        : -1;
    const menuColumn = menuColumnIndex >= 0 ? attrs.columns[menuColumnIndex] : null;
    React.useEffect(() => {
        if (
            expandedColumnId === null
            || attrs.columns.some(column => column.id === expandedColumnId)
        ) return;
        setExpandedColumnId(null);
    }, [attrs.columns, expandedColumnId]);

    const update = (next: Partial<PrintoutDataListAttrs>) =>
        updateSelectedPrintoutBlockAttributes(editor, block, next);

    const setColumns = (columns: DataListColumn[]) => update({columns});

    const updateColumn = (id: string, patch: Partial<DataListColumn>) => setColumns(
        attrs.columns.map(column => (column.id === id ? {...column, ...patch} : column)),
    );

    // Rebind defaults without replacing authored labels or alignment.
    const rebindColumn = (column: DataListColumn, variableKey: string) => {
        const labelWasDefault = !column.label.trim()
            || column.label === getDataListColumnLabel(attrs.source, column.variableKey);
        const alignWasDefault = column.align === getDataListDefaultAlign(column.variableKey);

        updateColumn(column.id, {
            align: alignWasDefault ? getDataListDefaultAlign(variableKey) : column.align,
            dateFormat: undefined,
            label: labelWasDefault ? getDataListColumnLabel(attrs.source, variableKey) : column.label,
            variableKey,
        });
    };

    const orderVariables = (variables: readonly {key: string; label: string}[]): VariableOption[] => {
        const recommended = sourceDefinition.defaultColumnKeys
            .map(key => variables.find(variable => variable.key === key))
            .filter((variable): variable is {key: string; label: string} => Boolean(variable));
        const more = variables.filter(variable => !recommendedKeys.has(variable.key));

        return [...recommended, ...more].map(variable => ({
            ...variable,
            recommended: recommendedKeys.has(variable.key),
        }));
    };

    const addableVariables = orderVariables(
        sourceDefinition.variables.filter(variable => !usedKeys.has(variable.key)),
    );

    // Keep obsolete stored keys editable until the author replaces them.
    const columnVariables = (column: DataListColumn): VariableOption[] => {
        const options = sourceDefinition.variables
            .filter(variable => variable.key === column.variableKey || !usedKeys.has(variable.key));
        const withCurrent = options.some(variable => variable.key === column.variableKey)
            ? options
            : [{key: column.variableKey, label: getDataListVariableLabel(attrs.source, column.variableKey)}, ...options];

        return orderVariables(withCurrent);
    };

    const addColumn = (variableKey: string) => {
        const column: DataListColumn = {
            align: getDataListDefaultAlign(variableKey),
            cellFormat: {bold: false, italic: false},
            headerFormat: {bold: false, italic: false},
            id: createDataListId(),
            label: getDataListColumnLabel(attrs.source, variableKey),
            variableKey,
        };

        setColumns([...attrs.columns, column]);
        setExpandedColumnId(column.id);
        setShowAddField(false);
    };

    const moveColumn = (index: number, offset: number) => {
        const target = index + offset;
        if (target < 0 || target >= attrs.columns.length) return;

        const columns = [...attrs.columns];
        [columns[index], columns[target]] = [columns[target], columns[index]];
        setColumns(columns);
    };

    const deleteColumn = (index: number) => {
        if (attrs.columns.length <= 1 || index < 0) return;
        const columns = attrs.columns.filter((_column, columnIndex) => columnIndex !== index);
        setColumns(columns);
        setExpandedColumnId(current => (
            current === attrs.columns[index].id
                ? columns[Math.min(index, columns.length - 1)]?.id ?? null
                : current
        ));
    };

    const resetColumnFormatting = (column: DataListColumn) => updateColumn(column.id, {
        align: getDataListDefaultAlign(column.variableKey),
        cellFormat: {bold: false, italic: false},
        dateFormat: undefined,
        headerFormat: {bold: false, italic: false},
    });

    const renderVariableOption = (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: VariableOption,
    ) => (
        <li {...props} key={option.key}>
            {t(option.label)}
        </li>
    );

    return (
        <>
            <PanelField label={t('Source')}>
                <Typography sx={{fontSize: 13, fontWeight: 600}}>
                    {t(sourceDefinition.label)}
                </Typography>
            </PanelField>

            <PanelField label={t('Label')}>
                <TextField
                    disabled={disabled}
                    fullWidth
                    helperText={t('Optional label shown above the list.')}
                    onChange={(event) => update({title: event.target.value})}
                    size={'small'}
                    value={attrs.title}
                />
            </PanelField>

            <PanelField label={t('When empty')}>
                <ToggleButtonGroup
                    disabled={disabled}
                    exclusive
                    fullWidth
                    onChange={(_event, mode: 'message' | 'hide' | null) => {
                        if (mode) update({hideWhenEmpty: mode === 'hide'});
                    }}
                    size={'small'}
                    value={attrs.hideWhenEmpty ? 'hide' : 'message'}
                >
                    <ToggleButton sx={{textTransform: 'none'}} value={'message'}>
                        {t('Show message')}
                    </ToggleButton>
                    <ToggleButton sx={{textTransform: 'none'}} value={'hide'}>
                        {t('Hide section')}
                    </ToggleButton>
                </ToggleButtonGroup>
                <Collapse in={!attrs.hideWhenEmpty} unmountOnExit>
                    <Box pt={1.5}>
                        <PanelField label={t('Message')}>
                            <TextField
                                disabled={disabled}
                                fullWidth
                                minRows={2}
                                multiline
                                onChange={(event) => update({emptyText: event.target.value})}
                                size={'small'}
                                value={attrs.emptyText}
                            />
                        </PanelField>
                    </Box>
                </Collapse>
            </PanelField>

            {attrs.source === 'agendaItems' && (
                <PanelField label={t('Filters')}>
                    <Select
                        disabled={disabled}
                        displayEmpty
                        fullWidth
                        multiple
                        onChange={(event) => {
                            const value = event.target.value;
                            update({
                                sectorTypes: (typeof value === 'string' ? value.split(',') : value) as DataListAgendaSectorType[],
                            });
                        }}
                        renderValue={(selected) => {
                            const values = selected as DataListAgendaSectorType[];
                            return values.length
                                ? values.map(value => t(AGENDA_SECTOR_TYPE_LABELS[value])).join(', ')
                                : t('All sectors');
                        }}
                        size={'small'}
                        value={attrs.sectorTypes}
                    >
                        {DATA_LIST_AGENDA_SECTOR_TYPES.map(sectorType => (
                            <MenuItem key={sectorType} value={sectorType}>
                                <Checkbox checked={attrs.sectorTypes.includes(sectorType)} size={'small'}/>
                                <ListItemText primary={t(AGENDA_SECTOR_TYPE_LABELS[sectorType])}/>
                            </MenuItem>
                        ))}
                    </Select>
                    <PanelHint>{t('Leave the selection empty to include every sector.')}</PanelHint>
                </PanelField>
            )}

            <PanelField label={t('Columns')}>
                <Stack gap={1}>
                    {attrs.columns.map(column => {
                        const variableOptions = columnVariables(column);
                        const selectedVariable = variableOptions.find(option => (
                            option.key === column.variableKey
                        )) ?? variableOptions[0];
                        const variableKind = findPrintoutVariable(column.variableKey)?.attr.kind;
                        const expanded = expandedColumnId === column.id;

                        return (
                            <PrintoutSettingsAccordion
                                actionsLabel={t('Column actions')}
                                disabled={disabled}
                                expanded={expanded}
                                id={column.id}
                                idPrefix={'data-list-column'}
                                key={column.id}
                                onOpenMenu={(anchorEl) => setColumnMenu({anchorEl, columnId: column.id})}
                                onToggle={() => setExpandedColumnId(current => (
                                    current === column.id ? null : column.id
                                ))}
                                title={column.label.trim() || t(getDataListColumnLabel(attrs.source, column.variableKey))}
                            >
                                <Stack gap={2}>
                                    <PanelField label={t('Field')}>
                                        <Autocomplete
                                            disableClearable
                                            disabled={disabled}
                                            getOptionLabel={(option) => t(option.label)}
                                            groupBy={(option) => option.recommended ? t('Recommended') : t('More fields')}
                                            isOptionEqualToValue={(option, value) => option.key === value.key}
                                            onChange={(_event, option) => rebindColumn(column, option.key)}
                                            options={variableOptions}
                                            renderInput={(params) => <TextField {...params} size={'small'}/>}
                                            renderOption={renderVariableOption}
                                            value={selectedVariable}
                                        />
                                    </PanelField>

                                    <PanelField label={t('Label')}>
                                        <TextField
                                            disabled={disabled}
                                            fullWidth
                                            onChange={(event) => updateColumn(column.id, {label: event.target.value})}
                                            placeholder={t(getDataListColumnLabel(attrs.source, column.variableKey))}
                                            size={'small'}
                                            value={column.label}
                                        />
                                    </PanelField>

                                    {variableKind === 'date' && (
                                        <PanelField label={t('Date format')}>
                                            <TextField
                                                disabled={disabled}
                                                fullWidth
                                                onChange={(event) => updateColumn(column.id, {
                                                    dateFormat: event.target.value as PrintoutDateFormat,
                                                })}
                                                select
                                                size={'small'}
                                                value={column.dateFormat ?? 'numeric'}
                                            >
                                                {DATE_FORMAT_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {t(option.label)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </PanelField>
                                    )}

                                    <PanelField label={t('Formatting')}>
                                        <Stack alignItems={'flex-start'} direction={'row'} flexWrap={'wrap'} gap={1.5}>
                                            <Stack gap={0.25}>
                                                <RowCaption>{t('Alignment')}</RowCaption>
                                                <ToggleButtonGroup
                                                    disabled={disabled}
                                                    exclusive
                                                    onChange={(_event, align: DataListAlign | null) => {
                                                        if (align) updateColumn(column.id, {align});
                                                    }}
                                                    size={'small'}
                                                    value={column.align}
                                                >
                                                    {DATA_LIST_ALIGNS.map(align => (
                                                        <ToggleButton aria-label={t(align)} key={align} value={align}>
                                                            {ALIGN_ICONS[align]}
                                                        </ToggleButton>
                                                    ))}
                                                </ToggleButtonGroup>
                                            </Stack>

                                            <Stack gap={0.25}>
                                                <RowCaption>{t('Label')}</RowCaption>
                                                <FormatToggle
                                                    disabled={disabled}
                                                    onChange={(headerFormat) => updateColumn(column.id, {headerFormat})}
                                                    value={column.headerFormat}
                                                />
                                            </Stack>

                                            <Stack gap={0.25}>
                                                <RowCaption>{t('Values')}</RowCaption>
                                                <FormatToggle
                                                    disabled={disabled}
                                                    onChange={(cellFormat) => updateColumn(column.id, {cellFormat})}
                                                    value={column.cellFormat}
                                                />
                                            </Stack>
                                        </Stack>
                                    </PanelField>

                                </Stack>
                            </PrintoutSettingsAccordion>
                        );
                    })}
                </Stack>

                {addableVariables.length > 0 && (
                    <Box mt={1}>
                        <Button
                            disabled={disabled}
                            onClick={() => setShowAddField(show => !show)}
                            size={'small'}
                            startIcon={showAddField ? undefined : <AddIcon/>}
                            sx={{textTransform: 'none'}}
                            variant={'text'}
                        >
                            {t(showAddField ? 'Cancel' : 'Add column')}
                        </Button>
                        <Collapse in={showAddField} unmountOnExit>
                            <Box pt={1}>
                                <PanelField label={t('Field')}>
                                    <Autocomplete
                                        getOptionLabel={(option) => t(option.label)}
                                        groupBy={(option) => option.recommended ? t('Recommended') : t('More fields')}
                                        onChange={(_event, option) => {
                                            if (option) addColumn(option.key);
                                        }}
                                        openOnFocus
                                        options={addableVariables}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    'aria-label': t('Search fields'),
                                                    autoFocus: true,
                                                }}
                                                placeholder={t('Search fields')}
                                                size={'small'}
                                            />
                                        )}
                                        renderOption={renderVariableOption}
                                        value={null}
                                    />
                                </PanelField>
                            </Box>
                        </Collapse>
                    </Box>
                )}
            </PanelField>

            <Menu
                anchorEl={columnMenu?.anchorEl ?? null}
                onClose={() => setColumnMenu(null)}
                open={Boolean(columnMenu)}
            >
                <MenuItem
                    disabled={menuColumnIndex <= 0}
                    onClick={() => {
                        moveColumn(menuColumnIndex, -1);
                        setColumnMenu(null);
                    }}
                >
                    <ListItemIcon><KeyboardArrowUpIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Sort up')}</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={menuColumnIndex < 0 || menuColumnIndex >= attrs.columns.length - 1}
                    onClick={() => {
                        moveColumn(menuColumnIndex, 1);
                        setColumnMenu(null);
                    }}
                >
                    <ListItemIcon><KeyboardArrowDownIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Sort down')}</ListItemText>
                </MenuItem>
                <Divider/>
                <MenuItem
                    disabled={!menuColumn}
                    onClick={() => {
                        if (menuColumn) resetColumnFormatting(menuColumn);
                        setColumnMenu(null);
                    }}
                >
                    <ListItemIcon><RestartAltIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Reset formatting')}</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={attrs.columns.length <= 1 || menuColumnIndex < 0}
                    onClick={() => {
                        deleteColumn(menuColumnIndex);
                        setColumnMenu(null);
                    }}
                    sx={{color: 'error.main'}}
                >
                    <ListItemIcon sx={{color: 'inherit'}}>
                        <DeleteOutlineIcon fontSize={'small'}/>
                    </ListItemIcon>
                    <ListItemText>{t('Remove column')}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

export default DataListSettings;
